import { Location } from '@angular/common';
import { Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, Route, Router } from '@angular/router';
import { PageUrls } from '@constants/page-urls.constants';
import { ActivityDataDeleteResult } from '@models/activity.model';
import { ActivityStateService } from '@services/activity/state/activity-state.service';
import { ModalService } from '@services/modal/modal.service';
import { MyAssetsFilterStateService } from '@services/my-assets-filter-state/my-assets-filter-state.service';
import { NotificationsService } from '@services/notifications/notifications.service';
import { ShoppingService } from '@services/shopping/shopping.service';
import { TranslationService } from '@services/translation/translation.service';
import { NavigationService } from '@services/view/navigation-service/navigation.service';
import { Subscription, filter } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ActivityManagerService implements OnDestroy {
  private next_steps_map = new Map<string, string | undefined>();
  private next_step: string | undefined = undefined;
  private deleting_data: boolean = false;
  private subscriptions: Subscription = new Subscription();

  constructor(
    private activity_state_svc: ActivityStateService,
    private modal_svc: ModalService,
    private my_assets_filters_svc: MyAssetsFilterStateService,
    private navigation_svc: NavigationService,
    private notifications_svc: NotificationsService,
    private router: Router,
    private shopping_svc: ShoppingService,
    private translation_svc: TranslationService
  ) {
    this.subscriptions.add(
      this.router.events
        .pipe(filter((event) => event instanceof NavigationEnd)) // || event instanceof NavigationStart))
        .subscribe((event) => {
          const order_type_id: number = this.activity_state_svc.orderTypeId();
          const activity_path: string = `/shop/${order_type_id}/`;
          const path: string =
            event instanceof NavigationEnd
              ? event.urlAfterRedirects.split('?')[0].replace(/\/\d*$/, '')
              : '';
          const split_path: string[] = path.split('/');
          let page: string = '';
          split_path.forEach((r: string, index: number = 0) => {
            if (index === split_path.length - 1) page = r;
          });
          let route_order_type_id: number = -1;

          if (!order_type_id || !path.startsWith(activity_path)) {
            this.next_step = undefined;
            return;
          }

          if (this.next_steps_map.has(path)) {
            this.next_step = this.next_steps_map.get(path);
            return;
          }

          for (let i: number = 0; i < this.router.config.length; i++) {
            const route_path: string = (this.router.config[i]?.path ?? '').split('/')[0];

            if (route_path === 'shop') {
              this.router.config[i].children?.forEach((activity_type_route: Route) => {
                if (activity_type_route.path === order_type_id.toString()) {
                  activity_type_route.children?.forEach(
                    (activity_step_route: Route, index: number = 0) => {
                      if (
                        activity_step_route.path === page &&
                        activity_step_route.data &&
                        activity_step_route.data['order_type_id'] === order_type_id
                      ) {
                        route_order_type_id = activity_step_route.data['order_type_id'];
                        if (
                          route_path &&
                          route_order_type_id === order_type_id &&
                          path === `${activity_path}${page}`
                        ) {
                          const next_path: string = (
                            activity_type_route.children![index + 1]?.path ?? ''
                          ).split('/')[0];
                          this.next_steps_map.set(
                            path,
                            next_path && next_path !== '**' ? next_path : undefined
                          );
                        }
                        return;
                      }
                      index++;
                    }
                  );
                }
              });
            }

            if (route_order_type_id !== -1) break;
          }

          this.next_step = this.next_steps_map.get(path);
        })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  state(): ActivityStateService {
    return this.activity_state_svc;
  }

  currentOrderTypeId(): number {
    return this.activity_state_svc.orderTypeId() ?? 0;
  }

  navigateToNextStep(params?: Parameters<Router['navigate']>[1], reset: boolean = false): void {
    const order_type_id: number = this.activity_state_svc.orderTypeId();
    if (!order_type_id) {
      this.router.navigate([PageUrls.HOME]);
      return;
    }

    if (reset) this.next_step = undefined;

    this.router.navigate([`/shop/${order_type_id}/${this.next_step ?? ''}`], params);
  }

  navigateToReview(order_id: number): void {
    const order_type_id: number = this.activity_state_svc.orderTypeId();
    if (!order_type_id || !order_id) {
      this.baseCancel(PageUrls.HOME, Boolean(order_id));
      return;
    }

    this.router.navigate([`/shop/${order_type_id}/review`, order_id]);
  }

  navigateToConfirmation(order_id: number): void {
    this.baseCancel(order_id ? `/shop/order-confirmation/${order_id}` : PageUrls.HOME, !order_id);
  }

  cancelCurrentActivity(cancel_order: boolean = false): void {
    if (
      this.router.url.endsWith(PageUrls.LOGIN) ||
      this.router.url.endsWith(PageUrls.HOME) ||
      this.router.url.endsWith(PageUrls.LOGOUT)
    )
      return;

    if (
      !cancel_order &&
      (!this.activity_state_svc.displayWarningModal() || !this.router.url.startsWith('/shop/'))
    ) {
      this.baseCancel(PageUrls.HOME);
      return;
    }

    this.clearCart(cancel_order);
  }

  clearCart(cancel_order: boolean = false, location?: Location): void {
    this.modal_svc.dismissLastModal();
    this.modal_svc.confirmationDialog(
      {
        title: cancel_order
          ? this.translation_svc.translate('cancel_order')
          : this.translation_svc.translate('return_to_dashboard'),
        message: cancel_order
          ? this.translation_svc.translate('cancellation_of_order_via_cancel')
          : this.translation_svc.translate('cancellation_of_order_via_dashboard'),
        confirm_button_text: this.translation_svc.translate('continue'),
        cancel_button_text: cancel_order
          ? this.translation_svc.translate('back')
          : this.translation_svc.translate('cancel'),
        compact: cancel_order,
        onConfirm: () => {
          this.baseCancel(PageUrls.HOME);
        },
        onCancel: () => {
          if (location) location?.back();
        },
        onDismiss: () => {
          if (location) location?.back();
        }
      },
      {
        backdrop: 'static',
        centered: true
      }
    );
  }

  // Pass navigate: false when a navigation to the dashboard is already in progress
  // (canDeactivate guard / dashboard resolver) — navigating again from there re-enters
  // the guard and loops.
  clearCartInBackground(navigate: boolean = true): void {
    this.baseCancel(navigate ? PageUrls.HOME : undefined, true);
  }

  unauthorizedActivity(): void {
    if (
      this.router.url.endsWith(PageUrls.LOGIN) ||
      this.router.url.endsWith(PageUrls.HOME) ||
      this.router.url.endsWith(PageUrls.LOGOUT)
    )
      return;

    this.modal_svc.confirmationDialog(
      {
        title: this.translation_svc.translate('not_allowed_to_submit_order'),
        message: this.translation_svc.translate('masquerade_alert'),
        confirm_button_text: this.translation_svc.translate('return_to_dashboard'),
        cancel_button_text: this.translation_svc.translate('back'),
        hide_confirm_message: true,
        compact: true,
        onConfirm: () => {
          this.baseCancel(PageUrls.HOME);
        }
      },
      {
        backdrop: 'static',
        centered: true
      }
    );
  }

  cancel(): void {
    this.baseCancel(undefined, false);
  }

  reset(): void {
    this.baseCancel(undefined, true, true);
  }

  private baseCancel(
    navigate: string | undefined = undefined,
    delete_data: boolean = true,
    reset: boolean = false
  ): void {
    if (this.deleting_data) return;

    const order_type_id: number = this.currentOrderTypeId();
    this.my_assets_filters_svc.reset();
    this.notifications_svc.clearReadStateForActivity(order_type_id);
    this.next_step = undefined;

    if (!delete_data) {
      this.activity_state_svc.empty();
      if (navigate) this.navigateUnlessInProgress(navigate);
      return;
    }

    this.deleting_data = true;
    this.shopping_svc
      .deleteUserActivity(this.activity_state_svc.activityData())
      .subscribe((res: ActivityDataDeleteResult) => {
        this.deleting_data = false;
        this.activity_state_svc.unregisterActivityOrder(res);
        this.activity_state_svc[reset ? 'reset' : 'empty']();
        if (navigate) this.navigateUnlessInProgress(navigate);
      });
  }

  private navigateUnlessInProgress(navigate: string): void {
    if (this.navigation_svc.navigatingTo() === navigate) return;

    this.router.navigate([navigate]);
  }
}
